import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { brandIdentity } from '../theme/brandIdentity';
import { 
  getBrandingInfo, 
  createBranding, 
  updateBranding, 
  getUploadUrls, 
  getDownloadUrls,
  uploadFileToS3, 
  getContentType, 
  getFileExtension,
  clearCache 
} from '../services/brandingService';

function SetBranding() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('colors');
  
  // Estados de carga y datos
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para el branding personalizado - inicializado con valores por defecto
  const getDefaultBrandingData = () => ({
    // Información de la empresa
    companyName: localStorage.getItem('tenant_id') || '',
    companySlogan: '',
    
    // Logos
    mainLogo: null,
    faviconLogo: null,
    logoPreview: null,
    faviconPreview: null,
    
    // Colores
    colors: {
      primary: brandIdentity.colors.primary,
      primaryLight: brandIdentity.colors.primaryLight,
      primaryDark: brandIdentity.colors.primaryDark,
      secondary: brandIdentity.colors.secondary,
      accent: brandIdentity.colors.accent,
      textDark: brandIdentity.colors.textDark,
      textLight: brandIdentity.colors.textLight,
      backgroundLight: brandIdentity.colors.backgroundLight,
      white: brandIdentity.colors.white,
      // Colores de estado adicionales
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    
    // Tipografía
    typography: {
      fontFamily: brandIdentity.typography.fontFamily,
      sizes: { ...brandIdentity.typography.sizes }
    }
  });

  const [brandingData, setBrandingData] = useState(getDefaultBrandingData());

  // Estados de UI
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    mainColors: false,
    statusColors: true,
    textColors: true
  });

  // Cargar datos de branding al montar el componente
  useEffect(() => {
    loadBrandingData();
  }, []);

  // Función para cargar los datos de branding
  const loadBrandingData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[BRANDING] Cargando datos de branding...');
      const existingData = await getBrandingInfo();
      
      if (existingData) {
        console.log('[BRANDING] Datos encontrados, cargando...');
        setHasExistingData(true);
        
        // Mapear los datos básicos primero
        const newBrandingData = {
          companyName: existingData.company_info?.company_name || localStorage.getItem('tenant_id') || '',
          companySlogan: existingData.company_info?.company_slogan || '',
          
          // Los logos inicialmente son null hasta que se descarguen
          mainLogo: null,
          faviconLogo: null,
          logoPreview: null,
          faviconPreview: null,
          
          colors: {
            ...getDefaultBrandingData().colors,
            ...existingData.colors
          },
          
          typography: {
            fontFamily: existingData.typography?.font_family || brandIdentity.typography.fontFamily,
            sizes: existingData.typography?.font_sizes || brandIdentity.typography.sizes
          }
        };

        // Cargar las imágenes si existen URLs
        if (existingData.logos?.main_logo_url || existingData.logos?.favicon_url) {
          try {
            console.log('[BRANDING] Descargando URLs presignadas para mostrar imágenes...');
            const downloadUrls = await getDownloadUrls();
            
            if (downloadUrls) {
              if (downloadUrls.main_logo?.get_url) {
                newBrandingData.logoPreview = downloadUrls.main_logo.get_url;
                console.log('[BRANDING] Logo principal cargado para preview');
              }
              
              if (downloadUrls.favicon?.get_url) {
                newBrandingData.faviconPreview = downloadUrls.favicon.get_url;
                console.log('[BRANDING] Favicon cargado para preview');
              }
            }
          } catch (imageError) {
            console.warn('[BRANDING] Error cargando imágenes para preview:', imageError);
            // No es un error crítico, continuamos sin las imágenes
          }
        }
        
        setBrandingData(newBrandingData);
        setUnsavedChanges(false);
      } else {
        console.log('[BRANDING] No se encontraron datos, usando valores por defecto');
        setHasExistingData(false);
        // Los datos por defecto ya están establecidos
      }
    } catch (err) {
      console.error('[BRANDING] Error cargando datos:', err);
      setError('Error cargando la configuración de branding. Usando valores por defecto.');
      setHasExistingData(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para manejar cambios en el branding
  const updateBranding = (path, value) => {
    setBrandingData(prev => {
      const newData = { ...prev };
      const pathArray = path.split('.');
      let current = newData;
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        current = current[pathArray[i]];
      }
      current[pathArray[pathArray.length - 1]] = value;
      
      return newData;
    });
    setUnsavedChanges(true);
  };

  // Función para validar y limpiar códigos hexadecimales
  const cleanHexColor = (value) => {
    // Asegurar que siempre empiece con #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }
    
    // Solo permitir caracteres hexadecimales válidos y limitar a 7 caracteres
    value = value.substring(0, 7).toUpperCase();
    value = value.replace(/[^#0-9A-F]/g, '');
    
    // Si solo tenemos #, agregar al menos un cero
    if (value === '#') {
      value = '#0';
    }
    
    return value;
  };

  // Función para alternar secciones colapsables
  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Función para manejar subida de archivos
  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      alert('El archivo es muy grande. Por favor selecciona una imagen menor a 2MB.');
      return;
    }

    try {
      setIsUploading(true);
      
      // Mostrar preview inmediatamente
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        if (type === 'main') {
          updateBranding('mainLogo', file);
          updateBranding('logoPreview', result);
        } else if (type === 'favicon') {
          updateBranding('faviconLogo', file);
          updateBranding('faviconPreview', result);
        }
      };
      reader.readAsDataURL(file);

    } catch (error) {
      console.error('[BRANDING] Error manejando archivo:', error);
      alert('Error procesando el archivo. Por favor intenta de nuevo.');
    } finally {
      setIsUploading(false);
    }
  };

  // Función para resetear a valores por defecto
  const resetToDefaults = () => {
    if (window.confirm('¿Estás seguro de que deseas resetear todo el branding a los valores por defecto?')) {
      setBrandingData(getDefaultBrandingData());
      setUnsavedChanges(true); // Marcar como cambiado para permitir guardar
    }
  };

  // Función para subir logos a S3 si han cambiado
  const uploadLogosIfNeeded = async () => {
    const uploads = [];
    
    // Verificar si hay logos nuevos para subir
    if (brandingData.mainLogo) {
      uploads.push({ type: 'main', file: brandingData.mainLogo });
    }
    if (brandingData.faviconLogo) {
      uploads.push({ type: 'favicon', file: brandingData.faviconLogo });
    }
    
    if (uploads.length === 0) return {};
    
    console.log('[BRANDING] Subiendo', uploads.length, 'logo(s)...');
    
    // Obtener URLs de subida
    const extMain = brandingData.mainLogo ? getFileExtension(brandingData.mainLogo) : 'png';
    const extFavicon = brandingData.faviconLogo ? getFileExtension(brandingData.faviconLogo) : 'png';
    
    const uploadUrls = await getUploadUrls(extMain, extFavicon);
    const logoUrls = {};
    
    // Subir archivos
    for (const upload of uploads) {
      const { type, file } = upload;
      const contentType = getContentType(file);
      
      if (type === 'main' && uploadUrls.upload.main_logo) {
        await uploadFileToS3(file, uploadUrls.upload.main_logo.put_url, contentType);
        logoUrls.main_logo_url = uploadUrls.upload.main_logo.final_https_url;
        console.log('[BRANDING] Logo principal subido');
      } else if (type === 'favicon' && uploadUrls.upload.favicon) {
        await uploadFileToS3(file, uploadUrls.upload.favicon.put_url, contentType);
        logoUrls.favicon_url = uploadUrls.upload.favicon.final_https_url;
        console.log('[BRANDING] Favicon subido');
      }
    }
    
    return logoUrls;
  };

  // Función para preparar los datos para la API
  const prepareBrandingDataForAPI = (logoUrls = {}) => {
    const apiData = {
      company_info: {
        company_name: brandingData.companyName,
        company_slogan: brandingData.companySlogan
      },
      colors: brandingData.colors,
      typography: {
        font_family: brandingData.typography.fontFamily,
        font_sizes: brandingData.typography.sizes
      }
    };
    
    // Añadir extensiones si hay logos nuevos
    if (brandingData.mainLogo) {
      apiData.ext_main = getFileExtension(brandingData.mainLogo);
    }
    if (brandingData.faviconLogo) {
      apiData.ext_favicon = getFileExtension(brandingData.faviconLogo);
    }
    
    // Si hay logos subidos, incluir las URLs
    if (Object.keys(logoUrls).length > 0) {
      apiData.logos = logoUrls;
    }
    
    return apiData;
  };

  // Función para guardar cambios
  const saveBranding = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      console.log('[BRANDING] Iniciando proceso de guardado...');
      
      // 1. Subir logos si es necesario
      const logoUrls = await uploadLogosIfNeeded();
      
      // 2. Preparar datos para la API
      const apiData = prepareBrandingDataForAPI(logoUrls);
      
      console.log('[BRANDING] Datos preparados:', apiData);
      
      // 3. Crear o actualizar según corresponda
      let response;
      if (hasExistingData) {
        console.log('[BRANDING] Actualizando branding existente...');
        response = await updateBranding(apiData);
      } else {
        console.log('[BRANDING] Creando nuevo branding...');
        response = await createBranding(apiData);
        setHasExistingData(true);
      }
      
      console.log('[BRANDING] Guardado exitoso:', response);
      
      // 4. Actualizar estado local
      setUnsavedChanges(false);
      
      // Limpiar archivos locales ya que fueron subidos
      setBrandingData(prev => ({
        ...prev,
        mainLogo: null,
        faviconLogo: null
      }));
      
      alert('¡Branding guardado exitosamente!');
      
    } catch (error) {
      console.error('[BRANDING] Error guardando:', error);
      setError('Error guardando la configuración. Por favor intenta de nuevo.');
      alert('Error guardando la configuración: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Componente de selector de color
  const ColorPicker = ({ label, colorKey, description }) => {
    const handleHexChange = (e) => {
      const cleanedValue = cleanHexColor(e.target.value);
      updateBranding(`colors.${colorKey}`, cleanedValue);
    };

    return (
      <div className="color-picker-item">
        <div className="color-info">
          <label className="color-label">{label}</label>
          {description && <span className="color-description">{description}</span>}
        </div>
        <div className="color-input-group">
          <input
            type="color"
            value={brandingData.colors[colorKey]}
            onChange={(e) => updateBranding(`colors.${colorKey}`, e.target.value.toUpperCase())}
            className="color-input"
          />
          <input
            type="text"
            value={brandingData.colors[colorKey]}
            onChange={handleHexChange}
            className="color-text-input"
            placeholder="#000000"
            maxLength={7}
          />
        </div>
      </div>
    );
  };

  // Componente de preview de botón
  const PreviewButton = ({ children, variant = 'primary', size = 'medium' }) => {
    const getButtonStyle = () => {
      const baseStyle = {
        padding: size === 'small' ? '8px 16px' : '12px 24px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        fontSize: size === 'small' ? '14px' : '16px',
        fontWeight: '600',
        fontFamily: brandingData.typography.fontFamily,
        transition: 'all 0.2s ease'
      };

      switch (variant) {
        case 'primary':
          return {
            ...baseStyle,
            backgroundColor: brandingData.colors.primary,
            color: brandingData.colors.white
          };
        case 'secondary':
          return {
            ...baseStyle,
            backgroundColor: brandingData.colors.secondary,
            color: brandingData.colors.white
          };
        case 'outline':
          return {
            ...baseStyle,
            backgroundColor: 'transparent',
            color: brandingData.colors.primary,
            border: `2px solid ${brandingData.colors.primary}`
          };
        default:
          return baseStyle;
      }
    };

    return <button style={getButtonStyle()}>{children}</button>;
  };

  // Componente de preview para colores principales
  const MainColorsPreview = () => (
    <div className="color-preview-section">
      <h4>Vista Previa - Colores Principales</h4>
      <div className="preview-mockup">
        {/* Mockup de header */}
        <div 
          className="mockup-header"
          style={{
            backgroundColor: brandingData.colors.primary,
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}
        >
          <div style={{ color: brandingData.colors.white, fontWeight: '600', fontSize: '18px' }}>
            🏢 {brandingData.companyName || 'Tu Empresa'} - Dashboard
          </div>
        </div>

        {/* Botones de muestra */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button
            style={{
              backgroundColor: brandingData.colors.primary,
              color: brandingData.colors.white,
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500'
            }}
          >
            Botón Primario
          </button>
          <button
            style={{
              backgroundColor: brandingData.colors.secondary,
              color: brandingData.colors.white,
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '500'
            }}
          >
            Botón Secundario
          </button>
          <button
            style={{
              backgroundColor: 'transparent',
              color: brandingData.colors.primary,
              border: `2px solid ${brandingData.colors.primary}`,
              padding: '6px 14px',
              borderRadius: '6px',
              fontWeight: '500'
            }}
          >
            Outline
          </button>
        </div>

        {/* Elementos con acento */}
        <div 
          style={{
            backgroundColor: `${brandingData.colors.accent}15`,
            border: `1px solid ${brandingData.colors.accent}`,
            padding: '0.75rem',
            borderRadius: '6px',
            color: brandingData.colors.accent,
            fontWeight: '500'
          }}
        >
          💡 Elemento con color de acento - Información destacada
        </div>
      </div>
    </div>
  );

  // Componente de preview para colores de estado  
  const StatusColorsPreview = () => (
    <div className="color-preview-section">
      <h4>Vista Previa - Estados y Notificaciones</h4>
      <div className="preview-mockup">
        <div className="status-messages">
          <div 
            style={{
              backgroundColor: `${brandingData.colors.success}15`,
              border: `1px solid ${brandingData.colors.success}`,
              padding: '0.75rem',
              borderRadius: '6px',
              color: brandingData.colors.success,
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}
          >
            ✅ Transacción completada exitosamente
          </div>
          
          <div 
            style={{
              backgroundColor: `${brandingData.colors.warning}15`,
              border: `1px solid ${brandingData.colors.warning}`,
              padding: '0.75rem',
              borderRadius: '6px',
              color: brandingData.colors.warning,
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}
          >
            ⚠️ Revisa los datos antes de continuar
          </div>
          
          <div 
            style={{
              backgroundColor: `${brandingData.colors.error}15`,
              border: `1px solid ${brandingData.colors.error}`,
              padding: '0.75rem',
              borderRadius: '6px',
              color: brandingData.colors.error,
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}
          >
            ❌ Error al procesar la solicitud
          </div>
          
          <div 
            style={{
              backgroundColor: `${brandingData.colors.info}15`,
              border: `1px solid ${brandingData.colors.info}`,
              padding: '0.75rem',
              borderRadius: '6px',
              color: brandingData.colors.info,
              fontWeight: '500'
            }}
          >
            ℹ️ Nueva actualización disponible
          </div>
        </div>
      </div>
    </div>
  );

  // Componente de preview para colores de texto y fondo
  const TextColorsPreview = () => (
    <div className="color-preview-section">
      <h4>Vista Previa - Texto y Fondos</h4>
      <div className="preview-mockup">
        <div 
          style={{
            backgroundColor: brandingData.colors.backgroundLight,
            padding: '1.5rem',
            borderRadius: '8px',
            border: `1px solid ${brandingData.colors.backgroundLight}`
          }}
        >
          <div 
            style={{
              backgroundColor: brandingData.colors.white,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <h3 
              style={{
                color: brandingData.colors.textDark,
                margin: '0 0 0.5rem 0',
                fontSize: '1.25rem',
                fontWeight: '600'
              }}
            >
              Tarjeta de Información
            </h3>
            
            <p 
              style={{
                color: brandingData.colors.textLight,
                margin: '0 0 1rem 0',
                fontSize: '0.9rem'
              }}
            >
              Texto secundario - Subtítulos y descripciones
            </p>
            
            <p 
              style={{
                color: brandingData.colors.textDark,
                margin: '0',
                fontSize: '1rem',
                lineHeight: '1.5'
              }}
            >
              Contenido principal - Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
              Este texto utiliza el color principal para contenido legible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Componente de preview para apps móviles
  const MobileAppPreview = () => (
    <div className="color-preview-section mobile-preview">
      <h4>Vista Previa - App Móvil</h4>
      <div className="mobile-mockup">
        <div className="mobile-screen">
          {/* Status bar */}
          <div 
            className="mobile-status-bar"
            style={{ backgroundColor: brandingData.colors.primaryDark }}
          />
          
          {/* Header */}
          <div 
            className="mobile-header"
            style={{ backgroundColor: brandingData.colors.primary }}
          >
            <div style={{ color: brandingData.colors.white, fontWeight: '600' }}>
              {brandingData.companyName || 'Tu App'}
            </div>
          </div>
          
          {/* Content area */}
          <div 
            className="mobile-content"
            style={{ backgroundColor: brandingData.colors.backgroundLight }}
          >
            <div 
              className="mobile-card"
              style={{ backgroundColor: brandingData.colors.white }}
            >
              <div style={{ color: brandingData.colors.textDark, fontWeight: '600', marginBottom: '8px' }}>
                Dispositivo a evaluar
              </div>
              <div style={{ color: brandingData.colors.textLight, fontSize: '14px', marginBottom: '12px' }}>
                iPhone 13 Pro Max
              </div>
              <button
                className="mobile-button"
                style={{
                  backgroundColor: brandingData.colors.secondary,
                  color: brandingData.colors.white
                }}
              >
                Continuar Evaluación
              </button>
            </div>
            
            <div 
              className="mobile-card"
              style={{ backgroundColor: brandingData.colors.white }}
            >
              <div style={{ color: brandingData.colors.textDark, fontWeight: '600', marginBottom: '8px' }}>
                Valor estimado
              </div>
              <div 
                style={{ 
                  color: brandingData.colors.success, 
                  fontSize: '18px', 
                  fontWeight: '700',
                  marginBottom: '8px'
                }}
              >
                $450.000
              </div>
              <div style={{ color: brandingData.colors.textLight, fontSize: '14px' }}>
                + Bono especial disponible
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Componente de sección colapsable
  const CollapsibleSection = ({ sectionKey, title, description, children, defaultExpanded = false }) => {
    const isCollapsed = collapsedSections[sectionKey];
    
    return (
      <div className="collapsible-section">
        <div 
          className="section-header-collapsible"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="section-header-content">
            <h3 className="section-title">{title}</h3>
            {description && <p className="section-description">{description}</p>}
          </div>
          <div className="section-toggle">
            <span className={`toggle-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
              {isCollapsed ? '▶️' : '▼️'}
            </span>
          </div>
        </div>
        
        <div className={`section-content ${isCollapsed ? 'collapsed' : 'expanded'}`}>
          {children}
        </div>
      </div>
    );
  };

  // Componente de carga
  const LoadingSpinner = () => (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <div className="loading-text">Cargando configuración de branding...</div>
    </div>
  );

  // Mostrar loading si está cargando
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="branding-container" style={{ fontFamily: brandingData.typography.fontFamily }}>
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">
          Personalización de Marca
          {error && <div className="error-badge">⚠️ Error</div>}
          {isUploading && <div className="uploading-badge">📤 Subiendo...</div>}
          {isSaving && <div className="saving-badge">💾 Guardando...</div>}
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-outline" 
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? '✏️ Editar' : '👁️ Vista Previa'}
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </header>

      <div className="container">
        <div className="branding-layout">
          {/* Panel de navegación */}
          <div className="branding-nav">
            <div className="nav-header">
              <h3>Configuración</h3>
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
              {unsavedChanges && (
                <div className="unsaved-indicator">
                  <span className="dot"></span>
                  Cambios sin guardar
                </div>
              )}
            </div>
            
            <div className="nav-tabs">
              <button 
                className={`nav-tab ${activeTab === 'company' ? 'active' : ''}`}
                onClick={() => setActiveTab('company')}
              >
                🏢 Empresa
              </button>
              <button 
                className={`nav-tab ${activeTab === 'logos' ? 'active' : ''}`}
                onClick={() => setActiveTab('logos')}
              >
                🎨 Logos
              </button>
              <button 
                className={`nav-tab ${activeTab === 'colors' ? 'active' : ''}`}
                onClick={() => setActiveTab('colors')}
              >
                🌈 Colores
              </button>
              <button 
                className={`nav-tab ${activeTab === 'typography' ? 'active' : ''}`}
                onClick={() => setActiveTab('typography')}
              >
                ✍️ Tipografía
              </button>
            </div>

            <div className="nav-actions">
              <button className="btn-action btn-reset" onClick={resetToDefaults}>
                🔄 Resetear
              </button>
              <button 
                className="btn-action btn-save" 
                onClick={saveBranding}
                disabled={!unsavedChanges || isSaving || isUploading}
              >
                {isSaving ? '🔄 Guardando...' : '💾 Guardar'}
              </button>
              
              {/* Botón de debug para limpiar caché - solo en desarrollo */}
              {process.env.NODE_ENV === 'development' && (
                <button 
                  className="btn-action btn-debug" 
                  onClick={() => {
                    clearCache();
                    alert('Caché limpiado');
                  }}
                  title="Limpiar caché (solo desarrollo)"
                >
                  🗑️ Debug
                </button>
              )}
            </div>
          </div>

          {/* Panel de contenido */}
          <div className="branding-content">
            {!previewMode ? (
              <>
                {/* Tab: Información de la empresa */}
                {activeTab === 'company' && (
                  <div className="tab-content">
                    <div className="section-header">
                      <h2>Información de la Empresa</h2>
                      <p>Configura el nombre y eslogan de tu empresa</p>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Nombre de la Empresa *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={brandingData.companyName}
                        onChange={(e) => updateBranding('companyName', e.target.value)}
                        placeholder="Ingresa el nombre de tu empresa"
                      />
                      <small className="form-help">Este nombre aparecerá en toda la aplicación</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Eslogan o Tagline</label>
                      <input
                        type="text"
                        className="form-input"
                        value={brandingData.companySlogan}
                        onChange={(e) => updateBranding('companySlogan', e.target.value)}
                        placeholder="Ej: Tu socio en tecnología circular"
                      />
                      <small className="form-help">Frase que representa a tu empresa (opcional)</small>
                    </div>
                  </div>
                )}

                {/* Tab: Logos */}
                {activeTab === 'logos' && (
                  <div className="tab-content">
                    <div className="section-header">
                      <h2>Logos de la Empresa</h2>
                      <p>Sube los logos que representarán tu marca</p>
                    </div>

                    <div className="logo-section">
                      <h3>Logo Principal</h3>
                      <div className="logo-upload-area">
                        <div className="logo-preview">
                          {brandingData.logoPreview ? (
                            <img src={brandingData.logoPreview} alt="Logo preview" />
                          ) : (
                            <div className="logo-placeholder">
                              <div className="placeholder-icon">🖼️</div>
                              <div>Logo Principal</div>
                              <small>Recomendado: 200x60px, PNG/SVG</small>
                            </div>
                          )}
                        </div>
                        <div className="logo-actions">
                          <button 
                            className="btn btn-primary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                          >
                            {isUploading ? '🔄 Procesando...' : '📁 Subir Logo'}
                          </button>
                          {brandingData.logoPreview && (
                            <button 
                              className="btn btn-outline"
                              onClick={() => {
                                updateBranding('mainLogo', null);
                                updateBranding('logoPreview', null);
                              }}
                              disabled={isUploading}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="logo-section">
                      <h3>Favicon</h3>
                      <div className="logo-upload-area favicon">
                        <div className="logo-preview small">
                          {brandingData.faviconPreview ? (
                            <img src={brandingData.faviconPreview} alt="Favicon preview" />
                          ) : (
                            <div className="logo-placeholder">
                              <div className="placeholder-icon">🔖</div>
                              <div>Favicon</div>
                              <small>32x32px, PNG/ICO</small>
                            </div>
                          )}
                        </div>
                        <div className="logo-actions">
                          <button 
                            className="btn btn-secondary"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e) => handleFileUpload(e, 'favicon');
                              input.click();
                            }}
                            disabled={isUploading}
                          >
                            {isUploading ? '🔄 Procesando...' : '📁 Subir Favicon'}
                          </button>
                          {brandingData.faviconPreview && (
                            <button 
                              className="btn btn-outline"
                              onClick={() => {
                                updateBranding('faviconLogo', null);
                                updateBranding('faviconPreview', null);
                              }}
                              disabled={isUploading}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'main')}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}

                {/* Tab: Colores */}
                {activeTab === 'colors' && (
                  <div className="tab-content">
                    <div className="section-header">
                      <h2>Paleta de Colores</h2>
                      <p>Personaliza los colores de tu aplicación</p>
                    </div>

                    <CollapsibleSection 
                      sectionKey="mainColors" 
                      title="Colores Principales"
                      description="Colores primarios, secundarios y de acento para tu marca"
                    >
                      <div className="color-grid">
                        <ColorPicker 
                          label="Color Primario" 
                          colorKey="primary" 
                          description="Color principal de botones y elementos destacados"
                        />
                        <ColorPicker 
                          label="Primario Claro" 
                          colorKey="primaryLight" 
                          description="Variante clara del color primario"
                        />
                        <ColorPicker 
                          label="Primario Oscuro" 
                          colorKey="primaryDark" 
                          description="Variante oscura del color primario"
                        />
                        <ColorPicker 
                          label="Color Secundario" 
                          colorKey="secondary" 
                          description="Color para elementos secundarios"
                        />
                        <ColorPicker 
                          label="Color de Acento" 
                          colorKey="accent" 
                          description="Color para llamadas de atención"
                        />
                      </div>
                      <MainColorsPreview />
                    </CollapsibleSection>

                    <CollapsibleSection 
                      sectionKey="statusColors" 
                      title="Colores de Estado"
                      description="Colores para mensajes, notificaciones y estados del sistema"
                    >
                      <div className="color-grid">
                        <ColorPicker 
                          label="Éxito" 
                          colorKey="success" 
                          description="Color para mensajes de éxito"
                        />
                        <ColorPicker 
                          label="Advertencia" 
                          colorKey="warning" 
                          description="Color para advertencias"
                        />
                        <ColorPicker 
                          label="Error" 
                          colorKey="error" 
                          description="Color para errores"
                        />
                        <ColorPicker 
                          label="Información" 
                          colorKey="info" 
                          description="Color para información"
                        />
                      </div>
                      <StatusColorsPreview />
                    </CollapsibleSection>

                    <CollapsibleSection 
                      sectionKey="textColors" 
                      title="Colores de Texto y Fondo"
                      description="Colores para texto, fondos y superficies de la aplicación"
                    >
                      <div className="color-grid">
                        <ColorPicker 
                          label="Texto Oscuro" 
                          colorKey="textDark" 
                          description="Color principal del texto"
                        />
                        <ColorPicker 
                          label="Texto Claro" 
                          colorKey="textLight" 
                          description="Color para texto secundario"
                        />
                        <ColorPicker 
                          label="Fondo Claro" 
                          colorKey="backgroundLight" 
                          description="Color de fondo de la aplicación"
                        />
                        <ColorPicker 
                          label="Blanco" 
                          colorKey="white" 
                          description="Color blanco para tarjetas y elementos"
                        />
                      </div>
                      <TextColorsPreview />
                    </CollapsibleSection>

                    <MobileAppPreview />
                  </div>
                )}

                {/* Tab: Tipografía */}
                {activeTab === 'typography' && (
                  <div className="tab-content">
                    <div className="section-header">
                      <h2>Tipografía</h2>
                      <p>Configura las fuentes de tu aplicación</p>
                    </div>

                    <div className="typography-section">
                      <h3>Familia de Fuente</h3>
                      <div className="form-group">
                        <label className="form-label">Fuente Principal</label>
                        <select
                          className="form-select"
                          value={brandingData.typography.fontFamily}
                          onChange={(e) => updateBranding('typography.fontFamily', e.target.value)}
                        >
                          <option value="Poppins">Poppins (Recomendada)</option>
                          <option value="Inter">Inter</option>
                          <option value="Roboto">Roboto</option>
                          <option value="Open Sans">Open Sans</option>
                          <option value="Lato">Lato</option>
                          <option value="Montserrat">Montserrat</option>
                          <option value="Source Sans Pro">Source Sans Pro</option>
                          <option value="Nunito">Nunito</option>
                          <option value="Work Sans">Work Sans</option>
                          <option value="Outfit">Outfit</option>
                        </select>
                        <small className="form-help">
                          Todas las fuentes son compatibles con Flutter y aplicaciones web
                        </small>
                      </div>
                    </div>

                    <div className="typography-preview">
                      <h3>Vista Previa de Tipografía</h3>
                      <div className="font-samples" style={{ fontFamily: brandingData.typography.fontFamily }}>
                        <div className="font-sample">
                          <h1 style={{ fontSize: brandingData.typography.sizes.h1, color: brandingData.colors.textDark }}>
                            Título Principal H1
                          </h1>
                        </div>
                        <div className="font-sample">
                          <h2 style={{ fontSize: brandingData.typography.sizes.h2, color: brandingData.colors.textDark }}>
                            Subtítulo H2
                          </h2>
                        </div>
                        <div className="font-sample">
                          <p style={{ fontSize: brandingData.typography.sizes.body, color: brandingData.colors.textDark }}>
                            Texto del cuerpo - Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                          </p>
                        </div>
                        <div className="font-sample">
                          <small style={{ fontSize: brandingData.typography.sizes.small, color: brandingData.colors.textLight }}>
                            Texto pequeño para subtítulos y ayuda
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Vista previa completa */
              <div className="preview-mode">
                <div className="preview-header">
                  <h2>Vista Previa del Branding</h2>
                  <p>Así se verá tu aplicación con el nuevo branding aplicado</p>
                </div>

                <div className="preview-sections">
                  {/* Preview del header */}
                  <div className="preview-section">
                    <h3>Header de la Aplicación</h3>
                    <div 
                      className="preview-header-demo"
                      style={{
                        backgroundColor: brandingData.colors.white,
                        border: `1px solid ${brandingData.colors.backgroundLight}`,
                        padding: '1rem 2rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {brandingData.logoPreview ? (
                          <img 
                            src={brandingData.logoPreview} 
                            alt="Logo" 
                            style={{ height: '40px', objectFit: 'contain' }}
                          />
                        ) : (
                          <div 
                            style={{ 
                              color: brandingData.colors.primary, 
                              fontSize: '24px',
                              fontWeight: 'bold',
                              fontFamily: brandingData.typography.fontFamily
                            }}
                          >
                            ♻️ {brandingData.companyName}
                          </div>
                        )}
                        <div 
                          style={{ 
                            color: brandingData.colors.textDark,
                            fontFamily: brandingData.typography.fontFamily,
                            fontSize: brandingData.typography.sizes.h2
                          }}
                        >
                          Dashboard Ejecutivo
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <span 
                          style={{ 
                            color: brandingData.colors.textLight,
                            fontFamily: brandingData.typography.fontFamily
                          }}
                        >
                          Hola, Usuario
                        </span>
                        <PreviewButton variant="outline" size="small">Cerrar Sesión</PreviewButton>
                      </div>
                    </div>
                  </div>

                  {/* Preview de botones */}
                  <div className="preview-section">
                    <h3>Botones y Elementos</h3>
                    <div className="preview-buttons" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <PreviewButton variant="primary">Botón Primario</PreviewButton>
                      <PreviewButton variant="secondary">Botón Secundario</PreviewButton>
                      <PreviewButton variant="outline">Botón Outline</PreviewButton>
                    </div>
                  </div>

                  {/* Preview de colores de estado */}
                  <div className="preview-section">
                    <h3>Mensajes de Estado</h3>
                    <div className="preview-messages" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div 
                        style={{
                          padding: '12px 16px',
                          borderRadius: '6px',
                          backgroundColor: `${brandingData.colors.success}20`,
                          border: `1px solid ${brandingData.colors.success}`,
                          color: brandingData.colors.success,
                          fontFamily: brandingData.typography.fontFamily
                        }}
                      >
                        ✅ Mensaje de éxito - Operación completada correctamente
                      </div>
                      <div 
                        style={{
                          padding: '12px 16px',
                          borderRadius: '6px',
                          backgroundColor: `${brandingData.colors.warning}20`,
                          border: `1px solid ${brandingData.colors.warning}`,
                          color: brandingData.colors.warning,
                          fontFamily: brandingData.typography.fontFamily
                        }}
                      >
                        ⚠️ Mensaje de advertencia - Revisa esta información
                      </div>
                      <div 
                        style={{
                          padding: '12px 16px',
                          borderRadius: '6px',
                          backgroundColor: `${brandingData.colors.error}20`,
                          border: `1px solid ${brandingData.colors.error}`,
                          color: brandingData.colors.error,
                          fontFamily: brandingData.typography.fontFamily
                        }}
                      >
                        ❌ Mensaje de error - Algo salió mal
                      </div>
                      <div 
                        style={{
                          padding: '12px 16px',
                          borderRadius: '6px',
                          backgroundColor: `${brandingData.colors.info}20`,
                          border: `1px solid ${brandingData.colors.info}`,
                          color: brandingData.colors.info,
                          fontFamily: brandingData.typography.fontFamily
                        }}
                      >
                        ℹ️ Mensaje informativo - Información adicional
                      </div>
                    </div>
                  </div>

                  {/* Preview de card */}
                  <div className="preview-section">
                    <h3>Tarjetas y Contenido</h3>
                    <div 
                      style={{
                        backgroundColor: brandingData.colors.white,
                        border: `1px solid ${brandingData.colors.backgroundLight}`,
                        borderRadius: '8px',
                        padding: '2rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <h2 
                        style={{
                          color: brandingData.colors.textDark,
                          fontFamily: brandingData.typography.fontFamily,
                          fontSize: brandingData.typography.sizes.h1,
                          marginBottom: '0.5rem'
                        }}
                      >
                        {brandingData.companyName}
                      </h2>
                      {brandingData.companySlogan && (
                        <p 
                          style={{
                            color: brandingData.colors.textLight,
                            fontFamily: brandingData.typography.fontFamily,
                            fontSize: brandingData.typography.sizes.body,
                            marginBottom: '1.5rem'
                          }}
                        >
                          {brandingData.companySlogan}
                        </p>
                      )}
                      <p 
                        style={{
                          color: brandingData.colors.textDark,
                          fontFamily: brandingData.typography.fontFamily,
                          fontSize: brandingData.typography.sizes.body,
                          lineHeight: '1.6',
                          marginBottom: '1.5rem'
                        }}
                      >
                        Bienvenido al sistema de gestión. Esta es una vista previa de cómo se verá 
                        tu aplicación con el branding personalizado aplicado.
                      </p>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <PreviewButton variant="primary">Acción Principal</PreviewButton>
                        <PreviewButton variant="outline">Acción Secundaria</PreviewButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .branding-container {
          min-height: 100vh;
          background-color: #F9FAFB;
        }

        .header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .header-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1F2937;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem;
        }

        .branding-layout {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 2rem;
          min-height: calc(100vh - 200px);
        }

        /* Panel de navegación */
        .branding-nav {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          height: fit-content;
          position: sticky;
          top: 2rem;
        }

        .nav-header {
          margin-bottom: 1.5rem;
        }

        .nav-header h3 {
          margin: 0 0 0.5rem 0;
          color: #1F2937;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .unsaved-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #F59E0B;
        }

        .unsaved-indicator .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #F59E0B;
          animation: pulse 2s infinite;
        }

        .nav-tabs {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 2rem;
        }

        .nav-tab {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.95rem;
          color: #6B7280;
          text-align: left;
        }

        .nav-tab:hover {
          background-color: #F3F4F6;
          color: #374151;
        }

        .nav-tab.active {
          background-color: #EEF2FF;
          color: #6366F1;
          font-weight: 500;
        }

        .nav-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding-top: 1.5rem;
          border-top: 1px solid #E5E7EB;
        }

        .btn-action {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .btn-reset {
          background-color: #F3F4F6;
          color: #6B7280;
        }

        .btn-reset:hover {
          background-color: #E5E7EB;
          color: #374151;
        }

        .btn-save {
          background-color: #6366F1;
          color: white;
        }

        .btn-save:hover:not(:disabled) {
          background-color: #4F46E5;
        }

        .btn-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Panel de contenido */
        .branding-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .tab-content {
          padding: 2rem;
        }

        .section-header {
          margin-bottom: 2rem;
          border-bottom: 1px solid #E5E7EB;
          padding-bottom: 1.5rem;
        }

        .section-header h2 {
          margin: 0 0 0.5rem 0;
          color: #1F2937;
          font-size: 1.75rem;
          font-weight: 600;
        }

        .section-header p {
          margin: 0;
          color: #6B7280;
          font-size: 1rem;
        }

        /* Formularios */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #374151;
        }

        .form-input,
        .form-select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s ease;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #6366F1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .form-help {
          display: block;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #6B7280;
        }

        /* Sección de logos */
        .logo-section {
          margin-bottom: 2rem;
        }

        .logo-section h3 {
          margin: 0 0 1rem 0;
          color: #374151;
          font-size: 1.25rem;
          font-weight: 500;
        }

        .logo-upload-area {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .logo-upload-area.favicon {
          align-items: center;
        }

        .logo-preview {
          width: 200px;
          height: 80px;
          border: 2px dashed #D1D5DB;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #F9FAFB;
          overflow: hidden;
        }

        .logo-preview.small {
          width: 64px;
          height: 64px;
        }

        .logo-preview img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .logo-placeholder {
          text-align: center;
          color: #6B7280;
          font-size: 0.875rem;
        }

        .placeholder-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .logo-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        /* Sección de colores */
        .color-section {
          margin-bottom: 2rem;
        }

        .color-section h3 {
          margin: 0 0 1rem 0;
          color: #374151;
          font-size: 1.25rem;
          font-weight: 500;
        }

        .color-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }

        .color-picker-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background-color: #F9FAFB;
        }

        .color-info {
          flex: 1;
        }

        .color-label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.25rem;
        }

        .color-description {
          display: block;
          font-size: 0.875rem;
          color: #6B7280;
        }

        .color-input-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .color-input {
          width: 50px;
          height: 50px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .color-text-input {
          width: 100px;
          padding: 0.5rem;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-family: monospace;
          font-size: 0.875rem;
        }

        /* Sección de tipografía */
        .typography-section {
          margin-bottom: 2rem;
        }

        .typography-section h3 {
          margin: 0 0 1rem 0;
          color: #374151;
          font-size: 1.25rem;
          font-weight: 500;
        }

        .typography-preview {
          margin-top: 2rem;
          padding: 2rem;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          background-color: #FAFAFA;
        }

        .typography-preview h3 {
          margin: 0 0 1.5rem 0;
          color: #374151;
        }

        .font-samples {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .font-sample {
          padding: 1rem;
          background: white;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }

        /* Vista previa */
        .preview-mode {
          padding: 2rem;
        }

        .preview-header {
          margin-bottom: 2rem;
          text-align: center;
        }

        .preview-header h2 {
          margin: 0 0 0.5rem 0;
          color: #1F2937;
          font-size: 2rem;
          font-weight: 600;
        }

        .preview-header p {
          margin: 0;
          color: #6B7280;
          font-size: 1.125rem;
        }

        .preview-sections {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .preview-section h3 {
          margin: 0 0 1rem 0;
          color: #374151;
          font-size: 1.25rem;
          font-weight: 500;
        }

        .preview-buttons {
          padding: 1.5rem;
          background-color: #F9FAFB;
          border-radius: 8px;
          border: 1px solid #E5E7EB;
        }

        .preview-messages {
          max-width: 600px;
        }

        /* Botones generales */
        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.95rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
        }

        .btn-primary {
          background-color: #6366F1;
          color: white;
        }

        .btn-primary:hover {
          background-color: #4F46E5;
        }

        .btn-secondary {
          background-color: #10B981;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #059669;
        }

        .btn-outline {
          background-color: transparent;
          color: #6366F1;
          border: 2px solid #6366F1;
        }

        .btn-outline:hover {
          background-color: #6366F1;
          color: white;
        }

        /* Secciones colapsables */
        .collapsible-section {
          margin-bottom: 1.5rem;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          overflow: hidden;
          background-color: #FFFFFF;
        }

        .section-header-collapsible {
          padding: 1.25rem;
          background-color: #F8FAFC;
          border-bottom: 1px solid #E5E7EB;
          cursor: pointer;
          transition: background-color 0.2s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
          user-select: none;
        }

        .section-header-collapsible:hover {
          background-color: #F1F5F9;
        }

        .section-title-group {
          flex: 1;
        }

        .section-title-group h3 {
          margin: 0 0 0.25rem 0;
          color: #1F2937;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .section-title-group p {
          margin: 0;
          color: #6B7280;
          font-size: 0.875rem;
        }

        .toggle-icon {
          font-size: 1.5rem;
          color: #6B7280;
          transition: transform 0.2s ease;
          margin-left: 1rem;
        }

        .toggle-icon.expanded {
          transform: rotate(180deg);
        }

        .section-content {
          overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s ease;
        }

        .section-content.collapsed {
          max-height: 0;
          padding: 0 1.25rem;
        }

        .section-content.expanded {
          max-height: 1000px;
          padding: 1.5rem 1.25rem;
        }

        /* Animaciones */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Componente de carga */
        .loading-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #F9FAFB;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #E5E7EB;
          border-left: 4px solid #6366F1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        .loading-text {
          color: #6B7280;
          font-size: 1rem;
          font-weight: 500;
        }

        /* Badges de estado */
        .error-badge,
        .uploading-badge,
        .saving-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-left: 0.5rem;
        }

        .error-badge {
          background-color: #FEE2E2;
          color: #DC2626;
        }

        .uploading-badge {
          background-color: #DBEAFE;
          color: #2563EB;
        }

        .saving-badge {
          background-color: #D1FAE5;
          color: #059669;
        }

        /* Mensaje de error */
        .error-message {
          background-color: #FEE2E2;
          color: #DC2626;
          border: 1px solid #FECACA;
          border-radius: 6px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .error-icon {
          flex-shrink: 0;
        }

        /* Botón de debug */
        .btn-debug {
          background-color: #FEF3C7;
          color: #92400E;
        }

        .btn-debug:hover {
          background-color: #FDE68A;
          color: #78350F;
        }

        /* Previsualizadores de colores */
        .color-preview-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background-color: #F8FAFC;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
        }

        .color-preview-section h4 {
          margin: 0 0 1rem 0;
          color: #475569;
          font-size: 1rem;
          font-weight: 600;
        }

        .preview-mockup {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
        }

        .status-messages {
          display: flex;
          flex-direction: column;
        }

        /* Preview móvil */
        .mobile-preview .mobile-mockup {
          display: flex;
          justify-content: center;
          padding: 2rem 0;
        }

        .mobile-screen {
          width: 280px;
          height: 500px;
          background: #000;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          position: relative;
        }

        .mobile-status-bar {
          height: 24px;
          width: 100%;
        }

        .mobile-header {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 20px;
        }

        .mobile-content {
          height: calc(100% - 84px);
          padding: 16px;
          overflow-y: auto;
        }

        .mobile-card {
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .mobile-button {
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .branding-layout {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .branding-nav {
            position: static;
            order: 2;
          }
          
          .branding-content {
            order: 1;
          }
          
          .color-grid {
            grid-template-columns: 1fr;
          }
          
          .logo-upload-area {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          
          .container {
            padding: 1rem;
          }

          .mobile-screen {
            width: 250px;
            height: 450px;
          }
        }

        @media (max-width: 640px) {
          .header {
            padding: 1rem;
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
          
          .header-actions {
            flex-direction: column;
            width: 100%;
          }
          
          .nav-tabs {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.5rem;
          }
          
          .nav-tab {
            text-align: center;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default SetBranding;